import {useEffect, useMemo, useState} from "react";
import useSWR from "swr";
import {api} from "../../api/apiProvider";
import urls from "../../api/urls";
import {ReportsFilter} from "./index";
import dayjs from "dayjs";
import {EngineReport, Product} from "../../api/types";
import {AllProductsReport} from "./AllProducts";

const dateFormat = 'YYYY-MM-DD';

export default function useReports(filter: ReportsFilter, selectedItem: AllProductsReport | undefined) {
  const [reports, setReports] = useState<AllProductsReport[]>([]);

  const getParamsString = () => {
    const params = new URLSearchParams([
      ['t[]', 'dict'],
      ['t[]', 'report:factors'],
      ['t[]', 'factor:name'],
      ['t[]', 'factor:channel'],
    ]);

    if (filter.pools) {
      for (let pool of filter.pools) {
        params.append('brief.products.company.pool.id[]', String(pool.value))
      }
    }

    if (filter.companies) {
      for (let company of filter.companies) {
        params.append('brief.products.company.id[]', String(company.value))
      }
    }

    if (filter.products) {
      for (let product of filter.products) {
        params.append('brief.products.id[]', String(product.value))
      }
    }

    if (filter.kpis) {
      for (let kpi of filter.kpis) {
        params.append('brief.kpi.id[]', String(kpi.value))
      }
    }

    if (filter.channels) {
      for (let channelId of filter.channels) {
        params.append('filterChannels[]', String(channelId))
      }
    }

    if (filter.period && filter.period[0] && filter.period[1] && filter.period[0] instanceof dayjs && filter.period[1] instanceof dayjs) {
      params.append('historicDateEnd[after]', String(dayjs(filter.period[0], 'DD.MM.YYYY').format(dateFormat)));
      params.append('historicDateStart[before]', String(dayjs(filter.period[1], 'DD.MM.YYYY').format(dateFormat)))
    }

    return params.toString()
  };

  const {
    data: reportsData,
    isValidating: isLoading,
  } = useSWR<{
    data: EngineReport[],
  }>((filter.companies && filter.companies.length > 0) ? `${urls.reports}?${getParamsString()}` : null, api.get, {
    revalidateOnFocus: false
  });

  const filteredReports = useMemo(() => {
    return selectedItem && selectedItem.id
      ? reports.filter((report: AllProductsReport) => report.id === selectedItem?.id)
      : reports
  }, [reports, selectedItem]);

  useEffect(() => {
    if (reportsData && reportsData.data) {
      let index = 1;

      const reports: AllProductsReport[] = [];

      for (let report of reportsData.data) {
        reports.push({
          id: report.id,
          engineId: report.engineId,
          number: index++,
          products: report.products.map((product: Product) => product.name),
          company: report.company.name,
          pool: report.poolCompany.name,
          kpi: report.kpi.name,
          version: report.version,
          part: report.kpiFactorShare.toFixed(2),
          period: report.historicDateStart + ' - ' + report.historicDateEnd,
          channels: report.campaignChannels,
        })
      }

      setReports(reports)
    }
  }, [reportsData]);

  return {
    reports: filteredReports,
    isLoading,
  }
}
